import { useEffect, useRef } from 'react';

interface StarfieldProps {
  /**
   * Density multiplier. 1.0 is the calibrated default (~1 star per
   * 12,000 px² of canvas area). Drop to 0.6 on dense sections where the
   * starfield is secondary to copy.
   */
  density?: number;
  /** Max distance in px at which two stars link into a constellation edge. */
  linkDistance?: number;
  /** Opacity ceiling for stars. Lines are half of this, mapped by distance. */
  intensity?: number;
  className?: string;
}

interface Star {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Radius of the star's outer points (khatam "spike length"). */
  r: number;
  phase: number;
  /** True ≈ 1 in 5 — drawn as Persian manuscript gold gilding instead of
   *  ink. These are the "bright stars" of classical Persian astronomy
   *  (Soheyl/Canopus, Tir/Sirius) — visual accent for the scrying-bowl
   *  metaphor without tipping into the generic-particle-cloud cliché. */
  gold: boolean;
}

/**
 * Animated starfield — the "Cup of Jamshid" visual metaphor. Slow-drifting
 * 4-point khatam stars (the Persian manuscript "sparkle" motif, two
 * overlapping squares at 45°), occasional constellation edges appear when
 * two stars wander near each other and fade with distance. Subtle twinkle
 * via a phase-shifted sine per star.
 *
 * Design notes:
 * - Colors are sampled from `--color-ink` and `--color-accent` at mount.
 *   A fraction of stars render in gold leaf; the rest in warm white.
 *   Together they read as Persian illumination, not a generic swirl.
 * - Stars are 4-point khatam polygons, not circles — the geometric
 *   shape matters at small sizes: a circle at 1.5px is a pixel blur,
 *   a 4-point khatam at the same extent reads as a tiny sparkle.
 * - Canvas is DPR-aware; `ResizeObserver` re-seeds on layout changes.
 * - Honors `prefers-reduced-motion` — stars freeze but still render, so
 *   the hero background isn't empty for motion-sensitive users.
 * - Pauses on `visibilitychange` to preserve battery on background tabs.
 * - O(n²) constellation scan is fine at our density (~60 stars on
 *   1440px). If density is ever raised, spatial hashing belongs here.
 */
export function Starfield({
  density = 1,
  linkDistance = 140,
  intensity = 0.4,
  className,
}: StarfieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    const rawCtx = canvasEl.getContext('2d');
    if (!rawCtx) return;
    // Pin to non-null types — TypeScript re-widens control-flow narrowed
    // union types when they're captured by nested function declarations,
    // so `canvas.foo` inside seed/draw/frame would otherwise error.
    const canvas: HTMLCanvasElement = canvasEl;
    const ctx: CanvasRenderingContext2D = rawCtx;

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    let w = 0;
    let h = 0;
    let stars: Star[] = [];
    let rafId: number | null = null;
    let running = true;
    // Monotonic animation clock — accumulates only while `running` is true,
    // so a background-tab pause never introduces a visible twinkle jump
    // when the tab returns. Raw `t` from requestAnimationFrame keeps
    // advancing through throttling on some browsers, which would slingshot
    // the sine phase; tracking our own `elapsed` sidesteps that.
    let elapsed = 0;
    let lastT = 0;

    // Cache resolved palette colors once — marketing has a single theme.
    // Ink = warm white (deep-vellum canvas); accent = Persian gold leaf.
    const style = getComputedStyle(canvas);
    const inkColor =
      style.getPropertyValue('--color-ink').trim() || '#f5efe0';
    const goldColor =
      style.getPropertyValue('--color-accent').trim() || '#d4a24c';

    function seed() {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      if (w <= 0 || h <= 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.max(
        12,
        Math.round((w * h * density) / 12_000),
      );
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.09,
        vy: (Math.random() - 0.5) * 0.09,
        // Slightly larger than old circles so the khatam shape reads at a
        // glance. Gold stars get an extra boost — they're the named
        // bright stars, should stand out.
        r: 1.0 + Math.random() * 1.6,
        phase: Math.random() * Math.PI * 2,
        gold: Math.random() < 0.22,
      }));

      // Render once immediately. In reduced-motion mode this is the only
      // render; in animated mode it prevents a flicker between seed() and
      // the first scheduled frame after a resize.
      draw();
    }

    /**
     * 4-point khatam drawn as a filled 8-vertex polygon: four outer
     * tips at the cardinal directions (`r` from center) with four
     * inset "waist" vertices between them. Waist is tuned to
     * `r * 0.18` — tight enough that the shape reads as a Persian
     * manuscript sparkle rather than a plain plus sign.
     */
    function drawKhatam(
      x: number,
      y: number,
      r: number,
    ): void {
      const waist = r * 0.18;
      ctx.beginPath();
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + waist, y - waist);
      ctx.lineTo(x + r, y);
      ctx.lineTo(x + waist, y + waist);
      ctx.lineTo(x, y + r);
      ctx.lineTo(x - waist, y + waist);
      ctx.lineTo(x - r, y);
      ctx.lineTo(x - waist, y - waist);
      ctx.closePath();
      ctx.fill();
    }

    function draw() {
      ctx.clearRect(0, 0, w, h);

      for (const s of stars) {
        if (!reducedMotion) {
          s.x += s.vx;
          s.y += s.vy;
          if (s.x < -4) s.x = w + 4;
          else if (s.x > w + 4) s.x = -4;
          if (s.y < -4) s.y = h + 4;
          else if (s.y > h + 4) s.y = -4;
        }
        const twinkle = reducedMotion
          ? 1
          : 0.65 + 0.35 * Math.sin(s.phase + elapsed * 0.0011);
        // Gold stars burn brighter than ink stars — gives the field a
        // clear hierarchy (bright named stars vs background field)
        // instead of a uniform swarm.
        ctx.globalAlpha = intensity * twinkle * (s.gold ? 1.1 : 0.85);
        ctx.fillStyle = s.gold ? goldColor : inkColor;
        drawKhatam(s.x, s.y, s.r);
      }

      // Constellation edges — only the near pairs get connected, lines
      // fade with distance so the effect reads as "some stars happen to
      // line up," not as a rigid graph. Lines lean toward gold for a
      // subtle illumination thread against the deep canvas.
      for (let i = 0; i < stars.length; i++) {
        const a = stars[i]!;
        for (let j = i + 1; j < stars.length; j++) {
          const b = stars[j]!;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < linkDistance) {
            ctx.globalAlpha =
              intensity * 0.38 * (1 - d / linkDistance);
            ctx.strokeStyle = goldColor;
            ctx.lineWidth = 0.45;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      ctx.globalAlpha = 1;
    }

    function frame(t: number) {
      rafId = null;
      if (!running) return;
      if (lastT === 0) lastT = t;
      // Cap dt so a GC stall or returning-from-hidden doesn't slingshot
      // stars across the canvas (the lastT = 0 reset on resume also
      // guards against this, but this is defense-in-depth).
      const dt = Math.min(t - lastT, 100);
      lastT = t;
      elapsed += dt;
      draw();
      rafId = requestAnimationFrame(frame);
    }

    seed();

    const ro = new ResizeObserver(() => {
      seed();
    });
    ro.observe(canvas);

    function onVisibility() {
      const nowRunning = !document.hidden;
      running = nowRunning;
      if (nowRunning && !reducedMotion) {
        // Reset so dt starts fresh on resume — elapsed only advances
        // while the tab was actually visible.
        lastT = 0;
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(frame);
      }
    }
    document.addEventListener('visibilitychange', onVisibility);

    if (!reducedMotion) {
      rafId = requestAnimationFrame(frame);
    }
    // In reduced-motion mode the single `seed() → draw()` at startup (and
    // again on resize via ResizeObserver) is the whole render path — no
    // raf loop, no per-frame clear-and-redraw of a static scene.

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [density, linkDistance, intensity]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={className}
      style={{ display: 'block', width: '100%', height: '100%' }}
    />
  );
}
